import axiosInstance from "../../lib/axiosInstance";
import { toast } from "react-toastify";
import { AppDispatch } from "../store";
import { notificationActions } from "../slices/notification";
import { NotificationMapper } from "../../mappers/NotificationMapper";
import { getErrorMessage } from "../../utils/errorUtils";

import { NotificationRequestDto } from "../../shared/dtos/notification/NotificationRequestDto";
import { NotificationResponseDto } from "../../shared/dtos/notification/NotificationResponseDto";
import { NotificationSummaryDto } from "../../shared/dtos/notification/NotificationSummaryDto";
import {
  NotificationQueryDto,
  UserNotificationQueryDto,
} from "../../shared/dtos/notification/NotificationQueryDto";
import { ApiResponseDto } from "../../shared/dtos/common/ApiResponseDto";
import { PaginatedResponse } from "../../shared/dtos/common/PaginatedResponse";
import { ApiResponseStatus } from "../../shared/enums/ApiResponseStatus";

export const sendNotification =
  (values: NotificationRequestDto) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(notificationActions.setLoadingCreate());
    try {
      const { data } = await axiosInstance.post<
        ApiResponseDto<NotificationResponseDto>
      >("/notifications", values, {
        headers: { requiresAuth: true },
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Notification dispatched successfully");
        const summary = NotificationMapper.mapNotificationToSummary(data.data);
        dispatch(notificationActions.createSuccess(summary));
        dispatch(
          notificationActions.setServerStatus({
            operation: "create",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(notificationActions.setErrorCreate(message));
    }
  };

export const getUserNotifications =
  (params?: UserNotificationQueryDto) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(notificationActions.setLoadingFetchAll());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<PaginatedResponse<NotificationSummaryDto>>
      >("/notifications/user", {
        headers: { requiresAuth: true },
        params,
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(notificationActions.setItems(data.data.content));
        dispatch(notificationActions.setPagination(data.data.pagination));
      }
    } catch (error) {
      dispatch(notificationActions.setErrorFetchAll(getErrorMessage(error)));
    }
  };

export const getUserUnreadCount =
  () =>
  async (dispatch: AppDispatch): Promise<void> => {
    try {
      const { data } = await axiosInstance.get<ApiResponseDto<number>>(
        "/notifications/user/unread-count",
        {
          headers: { requiresAuth: true },
        },
      );
      if (
        data.status === ApiResponseStatus.SUCCESS &&
        typeof data.data === "number"
      ) {
        dispatch(notificationActions.setUnreadCount(data.data));
      }
    } catch (error) {
      console.error("Unread count fetch failed:", getErrorMessage(error));
    }
  };

export const getAllNotifications =
  (params?: NotificationQueryDto) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(notificationActions.setLoadingFetchAll());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<PaginatedResponse<NotificationSummaryDto>>
      >("/notifications", {
        headers: { requiresAuth: true },
        params,
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(notificationActions.setItems(data.data.content));
        dispatch(notificationActions.setPagination(data.data.pagination));
      }
    } catch (error) {
      dispatch(notificationActions.setErrorFetchAll(getErrorMessage(error)));
    }
  };

export const getNotificationById =
  (id: string) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(notificationActions.setLoadingFetchOne());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<NotificationResponseDto>
      >(`/notifications/${id}`, {
        headers: { requiresAuth: true },
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(notificationActions.setItem(data.data));
      }
    } catch (error) {
      dispatch(notificationActions.setErrorFetchOne(getErrorMessage(error)));
    }
  };

export const markNotificationAsRead =
  (id: string) =>
  async (dispatch: AppDispatch): Promise<void> => {
    try {
      const { data } = await axiosInstance.put<ApiResponseDto<void>>(
        `/notifications/read/${id}`,
        null,
        {
          headers: { requiresAuth: true },
        },
      );
      if (data.status === ApiResponseStatus.SUCCESS) {
        dispatch(notificationActions.markReadSuccess(id));
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

export const markAllNotificationsAsRead =
  () =>
  async (dispatch: AppDispatch): Promise<void> => {
    try {
      const { data } = await axiosInstance.put<ApiResponseDto<void>>(
        "/notifications/read-all",
        null,
        {
          headers: { requiresAuth: true },
        },
      );
      if (data.status === ApiResponseStatus.SUCCESS) {
        dispatch(notificationActions.markAllReadSuccess());
        toast.success(data.message || "All notifications marked as read");
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

export const deleteNotification =
  (id: string) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(notificationActions.setLoadingDelete());
    try {
      const { data } = await axiosInstance.delete<ApiResponseDto<void>>(
        `/notifications/${id}`,
        {
          headers: { requiresAuth: true },
        },
      );
      if (data.status === ApiResponseStatus.SUCCESS) {
        toast.success(data.message || "Notification deleted");
        dispatch(notificationActions.deleteSuccess(id));
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

export const resetNotificationStatus = () => (dispatch: AppDispatch) =>
  dispatch(notificationActions.setServerStatus(null));
